/**
 * Centralized profile completion calculation utility
 * This ensures consistent profile completion percentage across all pages
 */

// Helper function to get nested object values
const getNestedValue = (obj, path) => {
  return path.split(".").reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

// Check if a value is considered "complete"
const isValueComplete = (value) => {
  return value !== null && value !== undefined && value !== "";
};

/**
 * Calculate profile completion percentage for contractor profiles
 * Uses the most comprehensive field list to ensure consistency
 * @param {Object} contractorData - The contractor profile data
 * @returns {Object} - { completionPercentage: number, isComplete: boolean, missingFields: string[] }
 */
export const calculateProfileCompletion = (contractorData) => {
  if (!contractorData) {
    return {
      completionPercentage: 0,
      isComplete: false,
      missingFields: [],
    };
  }

  // Comprehensive list of required fields for profile completion
  const requiredFields = [
    // Basic company information
    { path: "contractorId", name: "Contractor ID" },
    { path: "companyName", name: "Company Name" },

    // Registration details
    { path: "registrationDetails.panNumber", name: "PAN Number" },
    { path: "registrationDetails.gstNumber", name: "GST Number" },
    { path: "registrationDetails.businessType", name: "Business Type" },
    { path: "registrationDetails.clraDetails.rcNumber", name: "RC Number" },
    {
      path: "registrationDetails.clraDetails.issueDate",
      name: "RC Issue Date",
    },
    {
      path: "registrationDetails.clraDetails.expiryDate",
      name: "RC Expiry Date",
    },
    {
      path: "registrationDetails.clraDetails.issuingAuthority",
      name: "RC Issuing Authority",
    },

    // Contact details
    {
      path: "contactDetails.primaryContact.name",
      name: "Primary Contact Name",
    },
    {
      path: "contactDetails.primaryContact.designation",
      name: "Primary Contact Designation",
    },
    {
      path: "contactDetails.primaryContact.phone",
      name: "Primary Contact Phone",
    },
    {
      path: "contactDetails.primaryContact.email",
      name: "Primary Contact Email",
    },

    // Address details
    {
      path: "contactDetails.address.registered.street",
      name: "Registered Address Street",
    },
    {
      path: "contactDetails.address.registered.city",
      name: "Registered Address City",
    },
    {
      path: "contactDetails.address.registered.state",
      name: "Registered Address State",
    },
    {
      path: "contactDetails.address.registered.pincode",
      name: "Registered Address Pincode",
    },

    // Bank details
    { path: "bankDetails.accountName", name: "Bank Account Name" },
    { path: "bankDetails.accountNumber", name: "Bank Account Number" },
    { path: "bankDetails.bankName", name: "Bank Name" },
    { path: "bankDetails.ifscCode", name: "IFSC Code" },

    // Contractor strength
    { path: "contractorStrength.totalEmployees", name: "Total Employees" },

    // Owner details
    { path: "ownerDetails.ownerName", name: "Owner Name" },
    { path: "ownerDetails.aadhaarNumber", name: "Owner Aadhaar Number" },
    { path: "ownerDetails.panNumber", name: "Owner PAN Number" },
    { path: "ownerDetails.phoneNumber", name: "Owner Phone Number" },
    { path: "ownerDetails.emailAddress", name: "Owner Email Address" },

    // License details
    { path: "licenseDetails.laborLicenseNumber", name: "Labor License Number" },
    {
      path: "licenseDetails.issuingAuthority",
      name: "License Issuing Authority",
    },
    { path: "licenseDetails.issueDate", name: "License Issue Date" },
    { path: "licenseDetails.validTill", name: "License Valid Till" },
    { path: "licenseDetails.licenseType", name: "License Type" },
    { path: "licenseDetails.maxWorkersAllowed", name: "Max Workers Allowed" },
  ];

  const completedFields = [];
  const missingFields = [];

  requiredFields.forEach((field) => {
    const value = getNestedValue(contractorData, field.path);
    if (isValueComplete(value)) {
      completedFields.push(field);
    } else {
      missingFields.push(field.name);
    }
  });

  const completionPercentage = Math.round(
    (completedFields.length / requiredFields.length) * 100
  );

  return {
    completionPercentage,
    isComplete: completionPercentage >= 100,
    missingFields,
    completedFields: completedFields.length,
    totalFields: requiredFields.length,
  };
};

/**
 * Get profile completion status message based on percentage
 * @param {number} percentage - Completion percentage
 * @returns {string} - Status message
 */
export const getProfileCompletionMessage = (percentage) => {
  if (percentage < 30) {
    return "Complete basic information to get started";
  }
  if (percentage < 70) {
    return "Complete more details to improve profile";
  }
  if (percentage < 100) {
    return "Almost there! Complete remaining fields to activate your account";
  }
  return "Profile is complete! You can now access all features";
};

/**
 * Get profile completion color based on percentage
 * @param {number} percentage - Completion percentage
 * @returns {string} - MUI color name
 */
export const getProfileCompletionColor = (percentage) => {
  if (percentage < 30) return "error";
  if (percentage < 70) return "warning";
  return "success";
};

/**
 * Check if contractor can add workers based on profile completion
 * @param {Object} contractorData - The contractor profile data
 * @returns {Object} - { canAddWorkers: boolean, reason: string }
 */
export const canContractorAddWorkers = (contractorData) => {
  const { completionPercentage, isComplete } =
    calculateProfileCompletion(contractorData);

  if (isComplete) {
    return {
      canAddWorkers: true,
      reason: "Profile is complete",
    };
  }

  return {
    canAddWorkers: false,
    reason: `Profile is only ${completionPercentage}% complete. Please complete your profile to add workers.`,
  };
};
